import React, { useState } from 'react';
import { Card, Form, Select, Button, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { submitQuestionnaire } from '../../services/questionnaireService';
import { logger } from '../../utils/logger';

const { Title, Text } = Typography;
const { Option } = Select;

interface QuestionnairePageProps {
  onComplete: () => void;
  userUuid: string;
}

interface QuestionnaireData {
  age: string;
  gender: string;
  trading_experience: string;
  watch_frequency: string;
}

const QuestionnairePage: React.FC<QuestionnairePageProps> = ({ onComplete, userUuid }) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: QuestionnaireData) => {
    setLoading(true);
    try {
      const result = await submitQuestionnaire({
        uuid: userUuid,
        ...values
      });

      if (result.success) {
        message.success(t('questionnaire.submitSuccess'));
        onComplete();
      } else {
        message.error(result.error || t('questionnaire.submitFailed'));
      }
    } catch (error) {
      logger.error('问卷提交错误:', error);
      message.error(t('questionnaire.submitFailed') + '，请重试');
    } finally {
      setLoading(false);
    }
  };

  const ageOptions = [
    { value: '18-24', label: t('questionnaire.ageOptions.18-24') },
    { value: '25-30', label: t('questionnaire.ageOptions.25-30') },
    { value: '31-40', label: t('questionnaire.ageOptions.31-40') },
    { value: '41-50', label: t('questionnaire.ageOptions.41-50') },
    { value: '51-60', label: t('questionnaire.ageOptions.51-60') },
    { value: '60+', label: t('questionnaire.ageOptions.60+') }
  ];

  const genderOptions = [
    { value: 'male', label: t('questionnaire.genderOptions.male') },
    { value: 'female', label: t('questionnaire.genderOptions.female') },
    { value: 'other', label: t('questionnaire.genderOptions.other') }
  ];

  const tradingExperienceOptions = [
    { value: '0-1年', label: t('questionnaire.tradingExperienceOptions.0-1年') },
    { value: '1-3年', label: t('questionnaire.tradingExperienceOptions.1-3年') },
    { value: '3-5年', label: t('questionnaire.tradingExperienceOptions.3-5年') },
    { value: '5-10年', label: t('questionnaire.tradingExperienceOptions.5-10年') },
    { value: '10年以上', label: t('questionnaire.tradingExperienceOptions.10年以上') }
  ];

  const watchFrequencyOptions = [
    { value: '每天', label: t('questionnaire.watchFrequencyOptions.每天') },
    { value: '每周几次', label: t('questionnaire.watchFrequencyOptions.每周几次') },
    { value: '偶尔', label: t('questionnaire.watchFrequencyOptions.偶尔') },
    { value: '很少', label: t('questionnaire.watchFrequencyOptions.很少') }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.appBackground,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '600px',
          background: theme.surfaceBackground,
          border: `1px solid ${theme.surfaceBorder}`,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ 
            color: theme.textPrimary,
            marginBottom: '8px'
          }}>
            {t('questionnaire.title')}
          </Title>
          <Text style={{ 
            color: theme.textSecondary,
            fontSize: '16px'
          }}>
            {t('questionnaire.subtitle')}
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: '24px' }}
        >
          <Form.Item
            name="age"
            label={<span style={{ color: theme.textPrimary, fontWeight: '500' }}>{t('questionnaire.age')}</span>}
            rules={[{ required: true, message: t('questionnaire.ageRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('questionnaire.age')}
              style={{
                background: theme.surfaceBackground,
                borderColor: theme.surfaceBorder,
                color: theme.textPrimary
              }}
            >
              {ageOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="gender"
            label={<span style={{ color: theme.textPrimary, fontWeight: '500' }}>{t('questionnaire.gender')}</span>}
            rules={[{ required: true, message: t('questionnaire.genderRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('questionnaire.gender')}
              style={{
                background: theme.surfaceBackground,
                borderColor: theme.surfaceBorder,
                color: theme.textPrimary
              }}
            >
              {genderOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="trading_experience"
            label={<span style={{ color: theme.textPrimary, fontWeight: '500' }}>{t('questionnaire.tradingExperience')}</span>}
            rules={[{ required: true, message: t('questionnaire.tradingExperienceRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('questionnaire.tradingExperience')}
              style={{
                background: theme.surfaceBackground,
                borderColor: theme.surfaceBorder,
                color: theme.textPrimary
              }}
            >
              {tradingExperienceOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="watch_frequency"
            label={<span style={{ color: theme.textPrimary, fontWeight: '500' }}>{t('questionnaire.watchFrequency')}</span>}
            rules={[{ required: true, message: t('questionnaire.watchFrequencyRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('questionnaire.watchFrequency')}
              style={{
                background: theme.surfaceBackground,
                borderColor: theme.surfaceBorder,
                color: theme.textPrimary
              }}
            >
              {watchFrequencyOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginTop: '32px', marginBottom: '0' }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{
                width: '100%',
                height: '48px',
                background: theme.accentPrimary,
                borderColor: theme.accentPrimary,
                color: theme.surfaceBackground,
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              {loading ? t('questionnaire.submitting') : t('questionnaire.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default QuestionnairePage;
